import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import SiteLayout, { Stripe } from './SiteLayout';
import Markdown from './Markdown';
import usePhotoAlbums from './usePhotoAlbums';
import HILLS from '../../content/hillsIndex';
import { links } from './siteLinks';

// The website's content pages, at their old Wix addresses (/faq,
// /hills-prescott, /old-newsletters-3 ...).
//
//  - Editable pages (introduction, FAQ, the hills ...) come from the backend,
//    which serves what was last saved in Web Admin, or the starting text.
//    The hills page's grid of venues and each hill's photo albums are added
//    below the text here.
//  - Fixed pages (archive, newsletters) are Markdown files in src/content/,
//    built into the site.

const FIXED = Object.fromEntries(
  Object.entries(import.meta.glob('../../content/*.md', { query: '?raw', import: 'default', eager: true }))
    .map(([path, text]) => [path.split('/').pop().replace(/\.md$/, ''), text]),
);

const EDITABLE = ['introduction', 'faq', 'classes', 'give-it-a-go', 'rules', 'membership', 'privacy-statement',
  'hills', ...HILLS.map(h => h.slug)];

const NEWSLETTERS = [
  { slug: 'old-newsletters', label: '1999' },
  { slug: 'old-newsletters-4', label: 'March and July 2000' },
  { slug: 'old-newsletters-3', label: 'October and December 2000' },
  { slug: 'old-newsletters-2', label: '2001' },
  { slug: 'old-newsletters-5', label: 'September 2001' },
  { slug: 'old-newsletters-6', label: '2002' },
];


// Old Wix addresses whose content now lives elsewhere, so old links and
// search results still land somewhere useful.
const REDIRECTS = {
  'event-photos': '/photos',
  'pictures-past': '/photos',
  '2017photos': '/photos',
  targettimes: '/target-times',
  'event-entries': '/calendar',
  'copy-of-calendar': '/calendar',
  'book-online': '/calendar',
  forms: '/contact',
  'results-years': '/results-hub',
  'championship-placings': '/results-hub',
  'road-legal-championship': '/results-hub',
  'records-options': '/records',
  'classic-records': '/records?Class=classic',
  home: links.home,
};

function redirectFor(slug) {
  if (REDIRECTS[slug]) return REDIRECTS[slug];
  if (/^\d{4}-photos$/.test(slug)) return '/photos';
  if (/-champion$|^open-class-|^\d{4}-fe-results$/.test(slug)) return '/results-hub';
  return null;
}

function splitTitle(text) {
  const [first, ...rest] = text.split('\n');
  return first.startsWith('# ')
    ? { title: first.slice(2).trim(), body: rest.join('\n') }
    : { title: '', body: text };
}

function kickerFor(slug) {
  if (slug === 'hills') return 'Venues';
  if (slug.startsWith('hills-')) return 'The hills';
  if (slug === 'archives' || slug.startsWith('old-') || slug.startsWith('peter-isaac')) return 'Archives';
  if (slug === 'privacy-statement') return 'The club';
  return 'Get started';
}

// The venues' cards. A card's picture can be changed in Web Admin (Hill
// pictures: {slug: {image, imageAlt}}); otherwise it's the old site's photo.
function HillGrid() {
  const [pictures, setPictures] = useState({});
  useEffect(() => {
    fetch('/api/site/pages/hill-pictures')
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (data) setPictures(JSON.parse(data.body_md)); })
      .catch(() => {});
  }, []);
  return (
    <div className="site-album-grid site-hill-grid">
      {HILLS.map(h => (
        <a key={h.slug} className="site-album" href={`/${h.slug}`}>
          <span className="site-album-photo"><img src={pictures[h.slug]?.image || h.image} alt="" loading="lazy" /></span>
          <span className="site-album-text"><span className="site-album-hill">{h.name}</span></span>
        </a>
      ))}
    </div>
  );
}

function HillPhotos({ slug }) {
  const { events } = usePhotoAlbums();
  const hill = HILLS.find(h => h.slug === slug);
  if (!hill) return null;
  const albums = events.filter(e => e.url && hill.match.some(m => e.hill.toLowerCase().startsWith(m)));
  return (
    <section className="site-prose site-hill-photos">
      <h2>Photos</h2>
      {albums.length > 0 ? (
        <ul>
          {albums.map(a => (
            <li key={`${a.date}-${a.hill}`}>
              <a href={a.url} target="_blank" rel="noreferrer">{a.title}</a> ({a.count} photos)
            </li>
          ))}
        </ul>
      ) : (
        <p>Photos of {hill.name} will appear here as they are added to the <a href="/photos">NHCA photo archive</a>.</p>
      )}
      <p><a href="/hills">← All the hills</a></p>
    </section>
  );
}

function NewsletterNav({ slug }) {
  return (
    <nav className="site-issue-nav" aria-label="Newsletter issues">
      <span>The Hillclimber:</span>
      {NEWSLETTERS.map(n => (
        n.slug === slug
          ? <strong key={n.slug} aria-current="page">{n.label}</strong>
          : <a key={n.slug} href={`/${n.slug}`}>{n.label}</a>
      ))}
    </nav>
  );
}

export default function SitePage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const redirect = redirectFor(slug);
  const fixed = FIXED[slug];
  const editable = EDITABLE.includes(slug);
  const [remote, setRemote] = useState(null);   // {title, body_md} | 'missing'

  useEffect(() => {
    if (!editable) return undefined;
    let cancelled = false;
    fetch(`/api/site/pages/${slug}`)
      .then(res => (res.ok ? res.json() : 'missing'))
      .catch(() => 'missing')
      .then(d => { if (!cancelled) setRemote(d); });
    return () => { cancelled = true; };
  }, [slug, editable]);

  let title = '';
  let body = null;
  if (fixed !== undefined) {
    ({ title, body } = splitTitle(fixed));
  } else if (editable && remote && remote !== 'missing') {
    ({ title } = remote);
    body = remote.body_md;
  }
  const notFound = (!fixed && !editable) || remote === 'missing';

  useEffect(() => {
    const previous = document.title;
    document.title = `${notFound ? 'Page not found' : (title || 'NHCA')} · National Hill Climb Association`;
    return () => { document.title = previous; };
  }, [title, notFound]);

  // A link to a section (/old-newsletters#october-1999): the browser looked
  // for it before the text was on the page, so scroll once it is.
  useEffect(() => {
    if (body === null || !window.location.hash) return;
    const target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
    if (target) target.scrollIntoView();
  }, [body]);

  const isNewsletter = NEWSLETTERS.some(n => n.slug === slug);
  if (redirect) return <Navigate to={redirect} replace />;

  return (
    <SiteLayout>
      <section className="site-dark site-page-head">
        <div className="site-wrap site-section-head">
          <div>
            <span className="site-kicker">{notFound ? 'Sorry' : kickerFor(slug)}</span>
            <h1 className="site-h2">{notFound ? 'Page not found' : (title || ' ')}</h1>
          </div>
        </div>
      </section>
      <Stripe />
      <div className="site-wrap site-page">
        {notFound ? (
          <div className="site-prose">
            <p>There&apos;s no page at this address. It may have moved when the website was rebuilt.</p>
            <p><a href={links.home}>Go to the home page</a></p>
          </div>
        ) : (
          <>
            {isNewsletter && <NewsletterNav slug={slug} />}
            {body === null ? <p className="site-loading">Loading…</p> : <Markdown>{body}</Markdown>}
            {slug === 'hills' && <HillGrid />}
            {slug.startsWith('hills-') && <HillPhotos slug={slug} />}
            {isNewsletter && <NewsletterNav slug={slug} />}
          </>
        )}
      </div>
    </SiteLayout>
  );
}
